/**
 * Top 5 empresas por comissao no periodo, com participacao e variacao.
 *
 * Decisoes nao obvias:
 *  1. Cinco linhas, nao um anel: com centenas de empresas o anel vira
 *     confete; cinco linhas com numero, barra de participacao e variacao se
 *     leem em um segundo. "Ver todas" rola ate o resumo por empresa.
 *  2. Cada linha tem o atalho "Filtrar": recorta a pagina inteira por aquela
 *     empresa (o mesmo filtro do topo).
 *  3. A nota de concentracao (quanto as tres maiores respondem) e o risco
 *     que o ranking mostra sem dizer: dependencia de poucas empresas.
 */

import { Button, CompanyLogo, Footnote, IconButton, ProgressBar, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { participacaoBps } from '../comparar';
import type { CompanyCommissionSummary } from '../types';
import { plural } from '../vocabulario';
import { Delta } from './Delta';
import { identidadeDaEmpresa } from './IdentidadeEmpresa';

const LIMITE = 5;
const TAMANHO_DO_TOPO = 3;

export function TopEmpresas({
  rows,
  companies,
  totalComissaoCents,
  empresaFiltrada,
  onFiltrar,
  onVerTodas,
  className = '',
}: {
  readonly rows: readonly CompanyCommissionSummary[];
  readonly companies: readonly CompanySummary[];
  readonly totalComissaoCents: number;
  readonly empresaFiltrada: string | null;
  readonly onFiltrar: (companyId: string | null) => void;
  readonly onVerTodas: () => void;
  readonly className?: string;
}): JSX.Element {
  const topo = rows.filter((r) => r.commissionCents > 0).slice(0, LIMITE);
  const somaTopo = rows.slice(0, TAMANHO_DO_TOPO).reduce((acc, r) => acc + r.commissionCents, 0);
  const concentracao = participacaoBps(somaTopo, totalComissaoCents);

  return (
    <SectionCard id="top-empresas" title="Top 5 empresas" icon="estrela" description="As que mais geraram comissão no período, com a participação e a variação." className={`scroll-mt-20 ${className}`}>
      <div className="flex flex-col gap-4">
        {empresaFiltrada !== null ? (
          <div className="flex flex-col items-start gap-2 rounded-xl border border-dashed border-edge bg-elevated px-4 py-5">
            <p className="text-sm text-ink-muted">Filtrando uma empresa: o ranking compara todas.</p>
            <Button variant="secondary" size="sm" icon="x" onClick={() => onFiltrar(null)}>
              Ver todas as empresas
            </Button>
          </div>
        ) : topo.length === 0 ? (
          <p className="rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center text-sm text-ink-subtle">
            Nenhuma empresa com comissão no período. A primeira venda recuperada já aparece aqui.
          </p>
        ) : (
          <ol className="flex flex-col gap-3">
            {topo.map((r, i) => {
              const identidade = identidadeDaEmpresa(r.companyId, companies, r.companyName);
              const peso = participacaoBps(r.commissionCents, totalComissaoCents);
              const feeVigente = companies.find((c) => c.id === r.companyId)?.feeBps ?? r.feeBps;
              return (
                <li key={r.companyId} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-3">
                    <span className="w-4 shrink-0 text-right text-xs tabular-nums text-ink-subtle">
                      {i + 1}
                      <span className="sr-only">º lugar:</span>
                    </span>
                    <CompanyLogo company={identidade} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{identidade.name}</p>
                      <p className="truncate text-xs text-ink-subtle">
                        {formatCount(r.conversionCount)} {plural(r.conversionCount, 'venda', 'vendas')} · {formatBRLFromCents(r.recoveredAmountCents)} recuperados ·{' '}
                        {formatPercentFromBps(feeVigente)} vigente
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end">
                      <span className="text-sm font-semibold tabular-nums text-ink">{formatBRLFromCents(r.commissionCents)}</span>
                      <Delta atual={r.commissionCents} anterior={r.previousCommissionCents} tipo="dinheiro" compacto />
                    </div>
                    <IconButton icon="filtro" label={`Filtrar a página por ${identidade.name}`} size="sm" onClick={() => onFiltrar(r.companyId)} />
                  </div>
                  <div className="flex items-center gap-2 pl-7">
                    <ProgressBar value={peso ?? 0} max={10_000} tone="brand" label={`Participação de ${identidade.name} na comissão`} />
                    <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-ink-subtle">{peso === null ? VALOR_AUSENTE : formatPercentFromBps(peso)}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Footnote>
            {empresaFiltrada !== null
              ? 'A concentração só faz sentido comparando todas as empresas.'
              : concentracao === null
                ? 'Sem comissão no recorte, não há concentração para medir.'
                : `As ${formatCount(Math.min(TAMANHO_DO_TOPO, rows.length))} maiores respondem por ${formatPercentFromBps(concentracao)} da comissão.`}
          </Footnote>
          <Button variant="ghost" size="sm" iconRight="seta-dir" onClick={onVerTodas}>
            Ver todas
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
